import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Cpu, Lock, Zap } from 'lucide-react';

const WhyChooseUs = () => {
  const features = [
    {
      icon: Cpu,
      title: "Cutting-Edge Architecture",
      description: "We deploy modern architectures optimized for speed, scalability, and performance."
    },
    {
      icon: Lock,
      title: "Enterprise-Grade Security",
      description: "Your infrastructure is hardened using industry-standard secure protocols."
    },
    {
      icon: Zap,
      title: "Ultra-Fast Optimization",
      description: "Performance engineering guarantees load speeds that leave your competition behind."
    }
  ];

  return (
    <section className="py-24 px-6 max-w-7xl mx-auto relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        
        {/* Left Side: Content */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-electric-green font-bold text-sm uppercase tracking-widest mb-2 block">
            Operational Excellence
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
            We engineer systems <br />
            <span className="text-gray-400">built for the future.</span>
          </h2>
          <p className="text-gray-400 mb-10 leading-relaxed max-w-lg">
            Our expert engineers treat every infrastructure project like a masterpiece, ensuring peak optimization, bulletproof safety layers, and high efficiency.
          </p>

          {/* Interactive Feature Steps */}
          <div className="space-y-6">
            {features.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="flex gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 group-hover:border-electric-green/40 group-hover:bg-electric-green/10 group-hover:text-electric-green transition-all duration-300 flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-1 group-hover:text-electric-green transition-colors duration-300">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed max-w-md">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Right Side: High-End Visual Mesh Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex justify-center lg:justify-end"
        >
          {/* Neon Glow Sphere background */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-electric-green/10 rounded-full blur-[100px] pointer-events-none" />

          {/* Glass Card Interface */}
          <div className="w-full max-w-md bg-[#1A1F29]/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden group hover:border-white/20 transition-all duration-500">
            <div className="absolute top-0 right-0 w-32 h-32 bg-electric-green/5 rounded-full blur-3xl pointer-events-none" />
            
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/5">
              <span className="text-xs text-gray-500 font-mono">SYSTEM STATUS</span>
              <span className="px-2 py-1 rounded bg-electric-green/10 border border-electric-green/20 text-electric-green text-[10px] font-bold tracking-wider uppercase animate-pulse">
                OPTIMIZED
              </span>
            </div>

            {/* Simulated UI Performance Graph */}
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="flex justify-between text-xs text-gray-400 mb-2">
                  <span>Edge Routing Latency</span>
                  <span className="text-electric-green font-mono">14ms</span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }} 
                    whileInView={{ width: "92%" }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    className="h-full bg-electric-green shadow-[0_0_10px_rgba(57,255,20,0.5)] rounded-full" 
                  />
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="flex justify-between text-xs text-gray-400 mb-2">
                  <span>Threat Containment Rate</span>
                  <span className="text-electric-green font-mono">100%</span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }} 
                    whileInView={{ width: "100%" }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
                    className="h-full bg-electric-green shadow-[0_0_10px_rgba(57,255,20,0.5)] rounded-full" 
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default WhyChooseUs;